import type {
	OfferingModel,
	PageModel,
} from '@sosocio/creator-types/database';
import { isBookTypeOffering } from './offering-groups';
import {
	BACKEND_ONLY_FIXED_PAGE_ID_PREFIX,
	type BackendOnlyFixedPageStateModel,
} from './page-constants';

function getFixedPageInsertionIndex(
	insertionBaseIndex: number,
	serialnumber: number,
): number {
	return Math.max(
		0,
		Math.min(
			insertionBaseIndex,
			insertionBaseIndex + serialnumber + 1,
		),
	);
}

export interface InsertBackendOnlyFixedPagesForViewingOptions {
	fixedPageStateModels: BackendOnlyFixedPageStateModel[];
	offeringModel: OfferingModel;
	pageModels: PageModel[];
}

export function insertBackendOnlyFixedPagesForViewing(
	options: InsertBackendOnlyFixedPagesForViewingOptions,
): void {
	const {
		fixedPageStateModels,
		offeringModel,
		pageModels,
	} = options;

	if (
		!isBookTypeOffering(offeringModel)
		|| !fixedPageStateModels.length
	) {
		return;
	}

	const trailingInsideCoverPagesCount = (
		offeringModel.startright
			? 1
			: 0
	);

	fixedPageStateModels.forEach((fixedPageStateModel) => {
		const insertionBaseIndex = pageModels.length - trailingInsideCoverPagesCount;
		const insertionIndex = getFixedPageInsertionIndex(
			insertionBaseIndex,
			fixedPageStateModel.serialnumber,
		);

		pageModels.splice(
			insertionIndex,
			0,
			{
				...fixedPageStateModel.pageModel,
				id: `${BACKEND_ONLY_FIXED_PAGE_ID_PREFIX}${fixedPageStateModel.id}`,
				editable: 0,
				movable: 0,
				quantity: 1,
				serialnumber: fixedPageStateModel.serialnumber,
			},
		);
	});
}

/**
 * Orders pages by persisted index, keeping each viewing-only page (index `-1`: virtual
 * inside covers, backend-only fixed pages) right after the page it follows in
 * `getPagesForViewing`. A comparator that returns 0 for `-1` is inconsistent, so
 * engines disagree: SpiderMonkey moved the inside covers to the front and broke
 * the photobook flip in Firefox.
 *
 * @param pageModels Pages in `getPagesForViewing` order, with persisted pages appended
 * @returns A new array ordered by persisted index, viewing-only pages kept in their slots
 */
export function sortPagesForViewing<T extends { index: number }>(pageModels: T[]): T[] {
	let precedingIndex = -1;

	return pageModels
		.map((pageModel, position) => {
			if (pageModel.index !== -1) {
				precedingIndex = pageModel.index;
			}

			return {
				pageModel,
				position,
				sortIndex: precedingIndex,
			};
		})
		.sort((a, b) => a.sortIndex - b.sortIndex || a.position - b.position)
		.map(({ pageModel }) => pageModel);
}
